import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { t } from "@lingui/macro";
import { MetricKeyEnum } from "@reactive-resume/schema";
import { useToast } from "@/client/hooks/use-toast";
import { useGenerateOptimizationQuestions } from "@/client/services/resume-optimizer/generate-questions";
import { useResumeOptimization } from "@/client/hooks/use-resume-optimization";
import { useDialog } from "@/client/stores/dialog";
import { useSubscribeToOptimizerQueue } from "@/client/services/resume-optimizer/subscribe-to-queue";
import { useOptimizationStatus } from "@/client/services/resume-optimizer/check-optimization-status";
import { useResumeStore } from "@/client/stores/resume";
import { Compare } from "@/client/pages/builder/_components/ai-optimize/compare";

// Add new type for metric details
type MetricDetail = {
  id: MetricKeyEnum;
  title: string;
  component: React.ReactNode;
};

interface AnalysisContextType {
  // States
  isSubscribed: boolean;
  events: any[];
  isDevMode: boolean;
  selectedMetric: MetricDetail | null;

  // Handlers
  setSelectedMetric: (metric: MetricDetail | null) => void;
  handleAIOptimize: () => Promise<void>;
  handleFocusedMetricOptimize: (metricId: string) => Promise<void>;
  handleOptimizationStart: (answers: Record<string, string>, focusedMetrics?: string[]) => Promise<void>;
  handleClose: () => void;

  // Optimization hooks data
  isLoadingOptimization: boolean;
  isOptimizing: boolean;
  optimizationError: any;
  optimizationData: any;
  deleteOptimizationData: () => Promise<boolean | undefined>;
  acceptOptimizationData: () => Promise<boolean | undefined>;
  refetchOptimization: () => Promise<any>;

  // Queue data
  queueStatus: string;
  resetQueue: () => void;

  // Generation states
  isGeneratingQuestions: boolean;

  // Status data
  optimizationStatus: any;
  statusCheckSuccess: boolean;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

export const AnalysisProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // States
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [isDevMode, setIsDevMode] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricDetail | null>(null);
  const [hasCompletedOptimization, setHasCompletedOptimization] = useState(false);
  const [hasJustCompletedSession, setHasJustCompletedSession] = useState(false);

  // Get resume ID from the store
  const resumeId = useResumeStore((state) => state.resume.id);

  // Check optimization status on mount (for page refresh scenarios)
  const { data: optimizationStatus, isSuccess: statusCheckSuccess } =
    useOptimizationStatus(resumeId);

  // Handle initial optimization status check (for page refresh recovery)
  useEffect(() => {
    if (statusCheckSuccess && optimizationStatus && resumeId) {
      console.log("🔍 [DEBUG] Initial optimization status check:", {
        isInProgress: optimizationStatus.isInProgress,
        hasCompletedResults: optimizationStatus.hasCompletedResults,
        hasJustCompletedSession,
        resumeId,
      });

      // Only re-subscribe if optimization is in progress AND we haven't just completed a session
      if (optimizationStatus.isInProgress && !isSubscribed && !hasJustCompletedSession) {
        console.log(
          "✨ [DEBUG] Found optimization in progress after page refresh, subscribing to queue",
        );
        setIsSubscribed(true);
        setEvents([]); // Reset events since we're starting fresh
      } else if (hasJustCompletedSession) {
        console.log(
          "🚫 [DEBUG] Skipping re-subscription - just completed optimization in this session",
        );
      }
    }
  }, [
    statusCheckSuccess,
    optimizationStatus,
    resumeId,
    isSubscribed,
    hasJustCompletedSession,
    toast,
  ]);

  // Track subscription state changes
  useEffect(() => {
    console.log("🔄 [DEBUG] Subscription state changed:", {
      isSubscribed,
      resumeId,
      eventsCount: events.length,
    });
  }, [isSubscribed, resumeId, events.length]);

  // AI Optimization hooks
  const { open: openQuestionsDialog } = useDialog("optimization-questions");
  const { generateQuestions, loading: isGeneratingQuestions } = useGenerateOptimizationQuestions();
  const {
    isLoadingOptimization,
    isOptimizing,
    startOptimization,
    error: optimizationError,
    optimizationData,
    deleteOptimizationData,
    acceptOptimizationData,
    setOptimization,
    refetch: refetchOptimization,
  } = useResumeOptimization();

  // Subscribe to optimizer queue conditionally
  const {
    events: queueEvents,
    status: queueStatus,
    reset: resetQueue,
  } = useSubscribeToOptimizerQueue(isSubscribed ? resumeId : "");

  // Merge queue events with our events state
  useEffect(() => {
    // Only update if queueEvents actually changed
    const isDifferent =
      events.length !== queueEvents.length || events.some((e, i) => e !== queueEvents[i]);

    if (queueEvents.length > 0 && isDifferent) {
      setEvents(queueEvents);
      const lastEvent = queueEvents[queueEvents.length - 1];
      // Handle completion
      if (lastEvent.type === "completed" && !hasCompletedOptimization) {
        setHasCompletedOptimization(true);
        setTimeout(async () => {
          console.log("🎯 [DEBUG] Queue completed, fetching optimization data...");
          await refetchOptimization();
          console.log("📡 [DEBUG] Optimization data fetched, unsubscribing from queue");
          setIsSubscribed(false);
          setEvents([]);
          setHasJustCompletedSession(true);
        }, 2000);
      }
    }
  }, [queueEvents, refetchOptimization, setOptimization, events, hasCompletedOptimization]);

  // AI Optimization handlers
  const handleAIOptimize = useCallback(async () => {
    console.log("🚀 [DEBUG] handleAIOptimize called", { resumeId });

    if (!resumeId) {
      console.warn("⚠️ [DEBUG] No resumeId available for optimization");
      return;
    }

    try {
      console.log("📝 [DEBUG] Generating optimization questions...");
      // First, generate optimization questions
      const questionsResponse = await generateQuestions({ id: resumeId });
      console.log("✅ [DEBUG] Questions generated successfully", {
        questionsCount: questionsResponse.questions?.length || 0,
      });

      // Open the questions dialog
      openQuestionsDialog("create", {
        id: "optimization-questions",
        item: {
          questions: questionsResponse.questions,
          onSubmit: handleOptimizationStart,
          isLoading: false,
        },
      });
      console.log("🎯 [DEBUG] Questions dialog opened");
    } catch (error: any) {
      console.error("❌ [DEBUG] Error generating questions:", error);
      toast({
        title: t`Failed to Generate Questions`,
        description: t`There was a problem generating optimization questions. Please try again.`,
        variant: "error",
      });
    }
  }, [resumeId, generateQuestions, openQuestionsDialog, toast]);

  const handleFocusedMetricOptimize = useCallback(
    async (metricId: string) => {
      console.log("🎯 [DEBUG] handleFocusedMetricOptimize called", { resumeId, metricId });

      if (!resumeId) {
        console.warn("⚠️ [DEBUG] No resumeId available for focused metric optimization");
        return;
      }

      try {
        console.log("🔄 [DEBUG] Resetting queue for focused optimization...");
        resetQueue();

        console.log("⚡ [DEBUG] Starting focused optimization process for metric:", metricId);
        const success = await startOptimization({}, [metricId]); // Empty answers, focused on specific metric
        console.log("📊 [DEBUG] Focused optimization start result:", { success });

        if (!success) {
          console.warn("⚠️ [DEBUG] Focused optimization start failed, not subscribing to queue");
          return;
        }

        console.log("📡 [DEBUG] Setting subscription to true for focused optimization...");
        setIsSubscribed(true);
        console.log("✅ [DEBUG] Successfully subscribed to focused optimization queue");
      } catch (error: any) {
        console.error("❌ [DEBUG] Focused optimization error:", error);
        toast({
          title: t`Optimization Failed`,
          description: t`There was a problem starting the focused optimization process.`,
          variant: "error",
        });
      }
    },
    [resumeId, resetQueue, startOptimization, toast],
  );

  const handleOptimizationStart = useCallback(
    async (answers: Record<string, string>, focusedMetrics?: string[]) => {
      console.log("🎯 [DEBUG] handleOptimizationStart called", {
        resumeId,
        answersCount: Object.keys(answers).length,
        answers,
        focusedMetrics,
      });

      if (!resumeId) {
        console.warn("⚠️ [DEBUG] No resumeId available for optimization start");
        return;
      }

      try {
        console.log("🔄 [DEBUG] Resetting queue...");
        resetQueue();

        console.log("⚡ [DEBUG] Starting optimization process...");
        const success = await startOptimization(answers, focusedMetrics);
        console.log("📊 [DEBUG] Optimization start result:", { success });

        if (!success) {
          console.warn("⚠️ [DEBUG] Optimization start failed, not subscribing to queue");
          return;
        }

        console.log("📡 [DEBUG] Setting subscription to true...");
        setIsSubscribed(true);
        console.log("✅ [DEBUG] Successfully subscribed to optimization queue");
      } catch (error: any) {
        console.error("❌ [DEBUG] Optimization error:", error);
        toast({
          title: t`Optimization Failed`,
          description: t`There was a problem starting the optimization process.`,
          variant: "error",
        });
      }
    },
    [resumeId, resetQueue, startOptimization, toast],
  );

  const handleClose = useCallback(() => {
    console.log("🔒 [DEBUG] handleClose called - resetting all optimization states");
    // Reset all optimization-related states
    setIsSubscribed(false);
    setIsDevMode(false);
    setSelectedMetric(null);
    setOptimization(null);
    setEvents([]);
    setHasCompletedOptimization(false);
    setHasJustCompletedSession(false);
    resetQueue();
    console.log("✅ [DEBUG] Successfully reset all optimization states");
  }, [setOptimization, resetQueue]);

  // Display queue connection status if subscribed
  useEffect(() => {
    console.log("📊 [DEBUG] Queue status useEffect triggered", {
      isSubscribed,
      queueStatus,
      resumeId,
    });

    if (isSubscribed && queueStatus !== "idle") {
      console.log("🔗 [DEBUG] Optimization queue status changed:", {
        status: queueStatus,
        isSubscribed,
        resumeId,
      });
    }

    if (isSubscribed && queueStatus === "idle") {
      console.log("⏸️ [DEBUG] Queue is idle while subscribed - possible connection issue");
    }
  }, [isSubscribed, queueStatus, resumeId]);

  // Development flag to show Progress component for testing
  const SHOW_PROGRESS_DEV = searchParams.get("dev-progress") === "true";

  // Handle dev mode subscription - only affects dev mode, not real optimizations
  useEffect(() => {
    console.log("🧪 [DEBUG] Dev progress useEffect triggered", {
      SHOW_PROGRESS_DEV,
      isSubscribed,
      eventsLength: events.length,
      isDevMode,
    });

    if (SHOW_PROGRESS_DEV && !isSubscribed) {
      console.log("🧪 [DEBUG] Enabling dev mode - subscribing to queue");
      setIsDevMode(true);
      setIsSubscribed(true);
    } else if (!SHOW_PROGRESS_DEV && isDevMode && isSubscribed) {
      // Only unsubscribe if we were explicitly in dev mode
      console.log("🧪 [DEBUG] Disabling dev mode - unsubscribing from queue");
      setIsDevMode(false);
      setIsSubscribed(false);
    }
  }, [SHOW_PROGRESS_DEV, isSubscribed, events.length, isDevMode]);

  // Memoize context value to prevent unnecessary rerenders
  const value = useMemo<AnalysisContextType>(
    () => ({
      // States
      isSubscribed,
      events,
      isDevMode,
      selectedMetric,

      // Handlers
      setSelectedMetric,
      handleAIOptimize,
      handleFocusedMetricOptimize,
      handleOptimizationStart,
      handleClose,

      // Optimization hooks data
      isLoadingOptimization,
      isOptimizing,
      optimizationError,
      optimizationData,
      deleteOptimizationData,
      acceptOptimizationData,
      refetchOptimization,

      // Queue data
      queueStatus,
      resetQueue,

      // Generation states
      isGeneratingQuestions,

      // Status data
      optimizationStatus,
      statusCheckSuccess,
    }),
    [
      isSubscribed,
      events,
      isDevMode,
      selectedMetric,
      hasJustCompletedSession,
      setSelectedMetric,
      handleAIOptimize,
      handleFocusedMetricOptimize,
      handleOptimizationStart,
      handleClose,
      isLoadingOptimization,
      isOptimizing,
      optimizationError,
      optimizationData,
      deleteOptimizationData,
      acceptOptimizationData,
      refetchOptimization,
      queueStatus,
      resetQueue,
      isGeneratingQuestions,
      optimizationStatus,
      statusCheckSuccess,
    ],
  );

  return (
    <AnalysisContext.Provider value={value}>
      {children}

      {/* Global Compare Component - appears when optimization data is available */}
      {optimizationData?.data && (
        <Compare
          optimizationData={optimizationData.data}
          onAccept={async () => {
            console.log("✅ [DEBUG] Global Compare - optimization accepted, saving changes");
            try {
              await acceptOptimizationData();
              console.log("✅ [DEBUG] Optimization data accepted and saved");
              setHasJustCompletedSession(false); // Clear the session flag since optimization is now complete
              
              // Add delay to ensure server transaction completes before any potential refetch
              console.log("⏳ [DEBUG] Adding delay to ensure server transaction completes");
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error) {
              console.error("❌ [DEBUG] Error accepting optimization data:", error);
            } finally {
              handleClose();
            }
          }}
          onReject={async () => {
            console.log("❌ [DEBUG] Global Compare - optimization rejected, deleting data");
            try {
              await deleteOptimizationData();
              console.log("✅ [DEBUG] Optimization data deleted");
              setHasJustCompletedSession(false); // Clear the session flag since optimization is now complete
              
              // Add delay to ensure server transaction completes before any potential refetch
              console.log("⏳ [DEBUG] Adding delay to ensure server transaction completes");
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error) {
              console.error("❌ [DEBUG] Error deleting optimization data:", error);
            } finally {
              handleClose();
            }
          }}
        />
      )}
    </AnalysisContext.Provider>
  );
};

export const useAnalysisContext = () => {
  const context = useContext(AnalysisContext);
  if (context === undefined) {
    throw new Error("useAnalysisContext must be used within an AnalysisProvider");
  }
  return context;
};
